/**
 * Pure H OS · Workstream 01 · Company Setup
 * =========================================
 * Single-page module that consumes /api/pureh/corporate-identity/*
 * Venture-scoped (defaults to ORVAN). Includes:
 *  - Venture selector
 *  - Health Score dial + missing / expired alerts
 *  - 11 tabs (General / Registration / Tax / Banking / Addresses /
 *             Contacts / Licenses / Government / Digital / Timeline / Health)
 *  - AI OCR uploader for CR / Tax / License (preview-then-confirm)
 *  - Export dropdown (Company Profile / DD Package / Corporate Info PDFs)
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Building2, FileText, Receipt, Landmark, MapPin, Users, ShieldCheck,
  Globe, LinkIcon, Clock, Activity, Download, RefreshCcw, Sparkles,
  Upload, Plus, Trash2, Save, AlertTriangle, CheckCircle2, Pin, Palette,
} from "lucide-react";
import { Card, SectionHeader, Button, TextInput, ScoreDial, EmptyState } from "../../components/lib";
import { api, corporateIdentity, CISection, CIExtractKind, CISnapshot, CIHealth, ApiError } from "../../services/api";
import BrandAssetsTab from "./BrandAssetsTab";

// ── Tab metadata ──────────────────────────────────────────────────
type TabKey =
  | "general" | "registration" | "tax" | "banking" | "addresses"
  | "contacts" | "licenses" | "government" | "digital" | "timeline" | "health"
  | "brand";

const TABS: { key: TabKey; label: string; Icon: any }[] = [
  { key: "general",      label: "General",       Icon: Building2 },
  { key: "registration", label: "Registration",  Icon: FileText },
  { key: "tax",          label: "Tax",           Icon: Receipt },
  { key: "banking",      label: "Banking",       Icon: Landmark },
  { key: "addresses",    label: "Addresses",     Icon: MapPin },
  { key: "contacts",     label: "Contacts",      Icon: Users },
  { key: "licenses",     label: "Licenses",      Icon: ShieldCheck },
  { key: "government",   label: "Government",    Icon: Globe },
  { key: "digital",      label: "Digital",       Icon: LinkIcon },
  { key: "brand",        label: "Brand Assets",  Icon: Palette },
  { key: "timeline",     label: "Timeline",      Icon: Clock },
  { key: "health",       label: "Health & AI",   Icon: Activity },
];

// ── Small helpers ─────────────────────────────────────────────────
function Field({ label, children, span = 1 }: { label: string; children: React.ReactNode; span?: 1 | 2 }) {
  return (
    <label className="flex flex-col gap-1" style={{ gridColumn: `span ${span} / span ${span}` }}>
      <span className="text-[10px] uppercase tracking-[0.16em] text-slate-500 font-semibold">{label}</span>
      {children}
    </label>
  );
}

function Pill({ tone = "neutral", children }: { tone?: "neutral" | "success" | "warning" | "danger" | "info"; children: React.ReactNode }) {
  const palette = {
    neutral: { bg: "var(--ph-neutral-tint)", fg: "var(--ph-ink-muted)" },
    success: { bg: "var(--ph-success-tint)", fg: "var(--ph-success)" },
    warning: { bg: "var(--ph-warning-tint)", fg: "var(--ph-warning)" },
    danger:  { bg: "var(--ph-danger-tint)",  fg: "var(--ph-danger)" },
    info:    { bg: "rgba(79,93,184,.10)",    fg: "var(--ph-ai)" },
  }[tone];
  return (
    <span
      className="inline-flex items-center px-2 py-0.5 text-[10px] font-semibold rounded"
      style={{ background: palette.bg, color: palette.fg, letterSpacing: "0.10em", textTransform: "uppercase" }}
    >
      {children}
    </span>
  );
}

function daysUntil(iso?: string): number | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (isNaN(d.getTime())) return null;
  return Math.round((d.getTime() - Date.now()) / (24 * 60 * 60 * 1000));
}

function fmtDate(iso?: string): string {
  if (!iso) return "—";
  try { return new Date(iso).toISOString().slice(0, 10); } catch { return iso; }
}

// ── Section drawer (create / edit multi-row) ─────────────────────
function SectionDrawer({
  open, onClose, title, fields, initial, onSave, ventureId, section,
}: {
  open: boolean; onClose: () => void; title: string;
  fields: Array<{ key: string; label: string; type?: "text" | "date" | "number" | "bool" | "select"; options?: string[] }>;
  initial: Record<string, unknown>;
  onSave: (row: Record<string, unknown>) => Promise<void>;
  ventureId: string; section: CISection;
}) {
  const [row, setRow] = useState<Record<string, unknown>>(initial);
  const [saving, setSaving] = useState(false);

  useEffect(() => { setRow(initial); }, [initial, open]);

  if (!open) return null;

  const set = (k: string, v: unknown) => setRow((r) => ({ ...r, [k]: v }));

  const handleSave = async () => {
    setSaving(true);
    try { await onSave(row); onClose(); } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-40 flex justify-end" data-testid={`drawer-${section}`} onClick={onClose}>
      <div className="absolute inset-0 bg-slate-900/40" />
      <aside
        className="relative h-full w-full max-w-xl overflow-y-auto bg-white shadow-2xl p-6"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-5">
          <div>
            <div className="text-[10px] uppercase tracking-[0.16em] text-slate-500 font-semibold">Venture · {ventureId}</div>
            <div className="text-lg font-semibold text-slate-900" style={{ fontFamily: "var(--ph-display-font)" }}>{title}</div>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700 text-sm" data-testid="drawer-close">✕ Close</button>
        </div>

        <div className="grid grid-cols-2 gap-3">
          {fields.map((f) => (
            <Field key={f.key} label={f.label} span={f.type === "text" && f.key.length > 8 ? 2 : 1}>
              {f.type === "bool" ? (
                <select
                  className="px-3 py-2 text-sm border rounded"
                  style={{ borderColor: "var(--ph-border-strong)" }}
                  data-testid={`field-${f.key}`}
                  value={String(row[f.key] ?? false)}
                  onChange={(e) => set(f.key, e.target.value === "true")}
                >
                  <option value="false">No</option>
                  <option value="true">Yes</option>
                </select>
              ) : f.type === "select" ? (
                <select
                  className="px-3 py-2 text-sm border rounded"
                  style={{ borderColor: "var(--ph-border-strong)" }}
                  data-testid={`field-${f.key}`}
                  value={String(row[f.key] ?? "")}
                  onChange={(e) => set(f.key, e.target.value)}
                >
                  <option value="">—</option>
                  {(f.options ?? []).map((o) => <option key={o} value={o}>{o}</option>)}
                </select>
              ) : (
                <input
                  type={f.type ?? "text"}
                  data-testid={`field-${f.key}`}
                  value={(row[f.key] as string | number | undefined) ?? ""}
                  onChange={(e) => set(f.key, f.type === "number" ? Number(e.target.value) : e.target.value)}
                  className="px-3 py-2 text-sm border rounded"
                  style={{ borderColor: "var(--ph-border-strong)" }}
                />
              )}
            </Field>
          ))}
        </div>

        <div className="mt-6 flex justify-end gap-2">
          <Button variant="secondary" onClick={onClose} testId="drawer-cancel">Cancel</Button>
          <Button variant="primary" onClick={handleSave} testId="drawer-save" disabled={saving}>
            <Save size={14} /> {saving ? "Saving…" : "Save"}
          </Button>
        </div>
      </aside>
    </div>
  );
}

// ── AI OCR uploader (preview → confirm) ──────────────────────────
function AIExtractButton({
  ventureId, kind, onConfirm, label = "Upload & Extract with AI",
}: {
  ventureId: string; kind: CIExtractKind;
  onConfirm: (fields: Record<string, unknown>) => Promise<void>;
  label?: string;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [loading, setLoading] = useState(false);
  const [preview, setPreview] = useState<Record<string, unknown> | null>(null);
  const [error, setError] = useState<string | null>(null);

  const trigger = () => inputRef.current?.click();

  const handleFile = async (file: File) => {
    setLoading(true); setError(null);
    try {
      const res = await corporateIdentity.extract(ventureId, kind, file);
      setPreview(res.extracted || {});
    } catch (e) {
      setError(e instanceof Error ? e.message : "AI extraction failed");
    } finally {
      setLoading(false);
    }
  };

  const editField = (k: string, v: unknown) => {
    setPreview((p) => ({ ...(p || {}), [k]: v }));
  };

  return (
    <>
      <input
        ref={inputRef} type="file" accept="image/*,application/pdf" hidden
        data-testid={`ai-file-${kind}`}
        onChange={(e) => { const f = e.target.files?.[0]; if (f) handleFile(f); e.target.value = ""; }}
      />
      <Button variant="secondary" onClick={trigger} testId={`ai-extract-${kind}`}>
        <Sparkles size={14} /> {loading ? "Extracting…" : label}
      </Button>

      {preview && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50" data-testid="ai-preview-modal">
          <Card className="w-full max-w-lg bg-white">
            <SectionHeader
              eyebrow="AI OCR · Review before saving"
              title={`Extracted fields · ${kind.toUpperCase()}`}
              action={<Pill tone="info"><Sparkles size={10} className="inline" /> Claude Vision</Pill>}
            />
            <div className="text-[11px] text-slate-500 mb-3">
              Review the extracted values. Edit anything that looks wrong before saving. Nothing is persisted until you click <em>Confirm & Save</em>.
            </div>
            <div className="grid grid-cols-2 gap-3 max-h-[45vh] overflow-y-auto pr-1">
              {Object.entries(preview).map(([k, v]) => (
                <Field key={k} label={k} span={Array.isArray(v) ? 2 : 1}>
                  <input
                    className="px-3 py-2 text-sm border rounded"
                    style={{ borderColor: "var(--ph-border-strong)" }}
                    data-testid={`ai-field-${k}`}
                    value={Array.isArray(v) ? v.join(", ") : String(v ?? "")}
                    onChange={(e) => editField(k, Array.isArray(v) ? e.target.value.split(",").map((s) => s.trim()).filter(Boolean) : e.target.value)}
                  />
                </Field>
              ))}
            </div>
            {error && <div className="mt-3 text-xs text-red-600">{error}</div>}
            <div className="mt-5 flex justify-end gap-2">
              <Button variant="secondary" onClick={() => setPreview(null)} testId="ai-preview-cancel">Discard</Button>
              <Button
                variant="primary" testId="ai-preview-confirm"
                onClick={async () => {
                  const clean = { ...preview };
                  // AI-generated → mark flag so downstream sees provenance
                  (clean as any).aiExtracted = true;
                  await onConfirm(clean as Record<string, unknown>);
                  setPreview(null);
                }}
              >
                <CheckCircle2 size={14} /> Confirm &amp; Save
              </Button>
            </div>
          </Card>
        </div>
      )}
    </>
  );
}

// ── Table primitive ──────────────────────────────────────────────
function DataTable({
  columns, rows, onRowClick, onDelete, testIdPrefix,
}: {
  columns: Array<{ key: string; label: string; render?: (row: any) => React.ReactNode }>;
  rows: any[];
  onRowClick?: (row: any) => void;
  onDelete?: (row: any) => void;
  testIdPrefix: string;
}) {
  if (!rows.length) return <EmptyState title="No records yet" hint="Add a new row or upload a document to auto-extract with AI." />;
  return (
    <div className="overflow-x-auto -mx-2 px-2">
      <table className="min-w-full text-sm" data-testid={`table-${testIdPrefix}`}>
        <thead>
          <tr className="text-left text-[10px] uppercase tracking-[0.16em] text-slate-500">
            {columns.map((c) => <th key={c.key} className="py-2 pr-3 font-semibold">{c.label}</th>)}
            {onDelete && <th className="py-2 pr-3"></th>}
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr
              key={r.id || i}
              onClick={() => onRowClick?.(r)}
              className="border-t hover:bg-slate-50 cursor-pointer"
              style={{ borderColor: "var(--ph-border-subtle)" }}
              data-testid={`${testIdPrefix}-row-${i}`}
            >
              {columns.map((c) => (
                <td key={c.key} className="py-2 pr-3 text-slate-700">
                  {c.render ? c.render(r) : (r[c.key] ?? "—")}
                </td>
              ))}
              {onDelete && (
                <td className="py-2 pr-3">
                  <button
                    onClick={(e) => { e.stopPropagation(); if (confirm("Delete this row?")) onDelete(r); }}
                    className="text-slate-400 hover:text-red-600"
                    data-testid={`${testIdPrefix}-delete-${i}`}
                  >
                    <Trash2 size={14} />
                  </button>
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ── Field definitions per section ────────────────────────────────
const SECTION_FIELDS: Record<CISection, Array<{ key: string; label: string; type?: any; options?: string[] }>> = {
  registration: [
    { key: "crNumber",             label: "CR Number" },
    { key: "issuingAuthority",     label: "Issuing Authority" },
    { key: "issueDate",            label: "Issue Date", type: "date" },
    { key: "expiryDate",           label: "Expiry Date", type: "date" },
    { key: "unifiedNumber",        label: "Unified Number" },
    { key: "commercialActivities", label: "Commercial Activities (comma-sep)" },
  ],
  tax: [
    { key: "taxNumber", label: "Tax Number" },
    { key: "vatNumber", label: "VAT Number" },
    { key: "taxOffice", label: "Tax Office" },
    { key: "vatRate",   label: "VAT Rate (%)", type: "number" },
    { key: "taxStatus", label: "Status", type: "select", options: ["active", "suspended", "closed"] },
  ],
  banking: [
    { key: "bank",          label: "Bank Name" },
    { key: "beneficiary",   label: "Beneficiary Name" },
    { key: "iban",          label: "IBAN" },
    { key: "accountNumber", label: "Account Number" },
    { key: "swift",         label: "SWIFT / BIC" },
    { key: "currency",      label: "Currency", type: "select", options: ["EGP", "USD", "EUR", "GBP", "SAR", "AED"] },
    { key: "purpose",       label: "Purpose", type: "select", options: ["operating", "treasury", "dividend", "payroll"] },
    { key: "isPrimary",     label: "Primary?", type: "bool" },
  ],
  addresses: [
    { key: "kind",       label: "Kind", type: "select", options: ["hq", "warehouse", "factory", "branch", "office", "retail"] },
    { key: "label",      label: "Label" },
    { key: "country",    label: "Country" },
    { key: "city",       label: "City" },
    { key: "district",   label: "District" },
    { key: "street",     label: "Street" },
    { key: "building",   label: "Building" },
    { key: "unit",       label: "Unit / Floor" },
    { key: "postalCode", label: "Postal Code" },
    { key: "googleMapsUrl", label: "Google Maps URL" },
    { key: "isPrimary",  label: "Primary?", type: "bool" },
  ],
  contacts: [
    { key: "role",     label: "Role", type: "select", options: ["ceo", "gm", "finance", "legal", "operations", "gro", "auditor", "lawFirm"] },
    { key: "name",     label: "Full Name" },
    { key: "email",    label: "Email" },
    { key: "phone",    label: "Phone" },
    { key: "whatsapp", label: "WhatsApp" },
    { key: "linkedIn", label: "LinkedIn" },
    { key: "notes",    label: "Notes" },
  ],
  licenses: [
    { key: "kind",          label: "Kind", type: "select", options: ["commercial", "manufacturing", "import", "export", "iso", "fda", "ifra", "msds", "other"] },
    { key: "licenseNumber", label: "License Number" },
    { key: "issuer",        label: "Issuer" },
    { key: "issueDate",     label: "Issue Date", type: "date" },
    { key: "expiryDate",    label: "Expiry Date", type: "date" },
    { key: "status",        label: "Status", type: "select", options: ["active", "expiring", "expired", "revoked"] },
    { key: "notes",         label: "Notes" },
  ],
  government: [
    { key: "portal",      label: "Portal", type: "select", options: ["eta", "tax_authority", "customs", "social_insurance", "commercial_registry", "municipality", "other"] },
    { key: "displayName", label: "Display Name" },
    { key: "accountRef",  label: "Account Reference" },
    { key: "status",      label: "Status", type: "select", options: ["active", "inactive", "locked"] },
    { key: "notes",       label: "Notes" },
  ],
  digital: [
    { key: "kind",   label: "Kind", type: "select", options: ["domain", "email", "website", "shopify", "meta", "google", "tiktok", "amazon", "noon", "gateway", "erp"] },
    { key: "handle", label: "Handle / Username" },
    { key: "url",    label: "URL" },
    { key: "status", label: "Status", type: "select", options: ["active", "inactive"] },
    { key: "notes",  label: "Notes" },
  ],
};

// ── Main component ───────────────────────────────────────────────
export default function CompanySetupLayout() {
  const [ventures, setVentures] = useState<Array<{ id: string; name: string; brand?: string }>>([]);
  const [ventureId, setVentureId] = useState<string>("orvan");
  const [tab, setTab] = useState<TabKey>("general");
  const [snapshot, setSnapshot] = useState<CISnapshot | null>(null);
  const [health, setHealth] = useState<CIHealth | null>(null);
  const [loading, setLoading] = useState(true);
  const [drawer, setDrawer] = useState<{ section: CISection; initial: Record<string, unknown> } | null>(null);
  const [showTimelineForm, setShowTimelineForm] = useState(false);
  const [flash, setFlash] = useState<string | null>(null);

  // Load ventures list
  useEffect(() => {
    (async () => {
      try {
        const list = await api.ventures();
        const norm = (list as any[]).map((v) => ({ id: v.id, name: v.name || v.brand || v.id, brand: v.brand }));
        setVentures(norm);
        // Prefer ORVAN default; else first
        const orvan = norm.find((v) => v.id === "orvan");
        setVentureId(orvan?.id || norm[0]?.id || "orvan");
      } catch {
        setVentures([{ id: "orvan", name: "ORVAN Perfumes" }]);
      }
    })();
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [snap, hl] = await Promise.all([
        corporateIdentity.snapshot(ventureId),
        corporateIdentity.getHealth(ventureId).catch(() => null),
      ]);
      setSnapshot(snap);
      setHealth(hl as CIHealth | null);
    } catch (e) {
      if (e instanceof ApiError && e.status === 403) {
        setSnapshot(null); setHealth(null);
        setFlash("You do not have access to this venture.");
      }
    } finally {
      setLoading(false);
    }
  }, [ventureId]);

  useEffect(() => { if (ventureId) load(); }, [ventureId, load]);

  const showFlash = (msg: string) => { setFlash(msg); setTimeout(() => setFlash(null), 3000); };

  const handleCreate = async (section: CISection, row: Record<string, unknown>) => {
    await corporateIdentity.createRow(ventureId, section, row);
    await load(); showFlash(`Added new ${section} record.`);
  };

  const handlePatch = async (section: CISection, id: string, patch: Record<string, unknown>) => {
    await corporateIdentity.patchRow(ventureId, section, id, patch);
    await load(); showFlash(`Updated ${section} record.`);
  };

  const handleDelete = async (section: CISection, id: string) => {
    await corporateIdentity.deleteRow(ventureId, section, id);
    await load(); showFlash(`Deleted ${section} record.`);
  };

  const handleRecomputeHealth = async () => {
    const hl = await corporateIdentity.recomputeHealth(ventureId);
    setHealth(hl); showFlash(`Health score recomputed: ${hl.healthScore}/100`);
  };

  const handleDownload = async (kind: "company-profile" | "dd-package" | "corporate-info") => {
    try {
      await corporateIdentity.downloadReport(ventureId, kind);
      showFlash(`Downloaded ${kind}.pdf`);
    } catch (e) {
      showFlash(e instanceof Error ? e.message : "Download failed");
    }
  };

  const openCreate = (section: CISection) => setDrawer({ section, initial: {} });
  const openEdit   = (section: CISection, row: any) => setDrawer({ section, initial: row });

  // ── Rendered sections ────────────────────────────────────────
  const profile = (snapshot?.profile ?? {}) as Record<string, any>;

  const [profileEdit, setProfileEdit] = useState<Record<string, any>>({});
  useEffect(() => { setProfileEdit(profile); }, [profile]);

  const patchProfile = async () => {
    await corporateIdentity.patchProfile(ventureId, profileEdit);
    await load(); showFlash("Company profile updated.");
  };

  const renderTab = () => {
    if (!snapshot) return <EmptyState title="No data" hint="Select a venture." />;

    if (tab === "brand") {
      return <BrandAssetsTab ventureId={ventureId} />;
    }

    if (tab === "general") {
      return (
        <Card>
          <SectionHeader
            eyebrow="Company · General Information"
            title={profile.nameEn || "Untitled Venture"}
            action={<Button variant="primary" onClick={patchProfile} testId="save-profile"><Save size={14} /> Save</Button>}
          />
          <div className="grid grid-cols-2 gap-4">
            <Field label="Name (English)"><input data-testid="field-nameEn" className="px-3 py-2 text-sm border rounded" style={{ borderColor: "var(--ph-border-strong)" }} value={profileEdit.nameEn ?? ""} onChange={(e) => setProfileEdit({ ...profileEdit, nameEn: e.target.value })} /></Field>
            <Field label="Name (Arabic)"><input dir="rtl" data-testid="field-nameAr" className="px-3 py-2 text-sm border rounded" style={{ borderColor: "var(--ph-border-strong)" }} value={profileEdit.nameAr ?? ""} onChange={(e) => setProfileEdit({ ...profileEdit, nameAr: e.target.value })} /></Field>
            <Field label="Commercial Name"><input data-testid="field-commercialName" className="px-3 py-2 text-sm border rounded" style={{ borderColor: "var(--ph-border-strong)" }} value={profileEdit.commercialName ?? ""} onChange={(e) => setProfileEdit({ ...profileEdit, commercialName: e.target.value })} /></Field>
            <Field label="Short Name"><input data-testid="field-shortName" className="px-3 py-2 text-sm border rounded" style={{ borderColor: "var(--ph-border-strong)" }} value={profileEdit.shortName ?? ""} onChange={(e) => setProfileEdit({ ...profileEdit, shortName: e.target.value })} /></Field>
            <Field label="Legal Entity">
              <select data-testid="field-legalEntity" className="px-3 py-2 text-sm border rounded" style={{ borderColor: "var(--ph-border-strong)" }} value={profileEdit.legalEntity ?? ""} onChange={(e) => setProfileEdit({ ...profileEdit, legalEntity: e.target.value })}>
                <option value="">—</option>
                <option>LLC</option><option>JSC</option><option>SPV</option><option>Sole Prop</option><option>Partnership</option>
              </select>
            </Field>
            <Field label="Industry"><input data-testid="field-industry" className="px-3 py-2 text-sm border rounded" style={{ borderColor: "var(--ph-border-strong)" }} value={profileEdit.industry ?? ""} onChange={(e) => setProfileEdit({ ...profileEdit, industry: e.target.value })} /></Field>
            <Field label="Business Activity" span={2}><input data-testid="field-businessActivity" className="px-3 py-2 text-sm border rounded" style={{ borderColor: "var(--ph-border-strong)" }} value={profileEdit.businessActivity ?? ""} onChange={(e) => setProfileEdit({ ...profileEdit, businessActivity: e.target.value })} /></Field>
            <Field label="Founded"><input type="date" data-testid="field-foundedAt" className="px-3 py-2 text-sm border rounded" style={{ borderColor: "var(--ph-border-strong)" }} value={profileEdit.foundedAt?.slice?.(0, 10) ?? ""} onChange={(e) => setProfileEdit({ ...profileEdit, foundedAt: e.target.value })} /></Field>
            <Field label="Country"><input data-testid="field-country" className="px-3 py-2 text-sm border rounded" style={{ borderColor: "var(--ph-border-strong)" }} value={profileEdit.country ?? ""} onChange={(e) => setProfileEdit({ ...profileEdit, country: e.target.value })} /></Field>
            <Field label="Base Currency"><input data-testid="field-baseCurrency" className="px-3 py-2 text-sm border rounded" style={{ borderColor: "var(--ph-border-strong)" }} value={profileEdit.baseCurrency ?? ""} onChange={(e) => setProfileEdit({ ...profileEdit, baseCurrency: e.target.value })} /></Field>
            <Field label="Timezone"><input data-testid="field-timezone" className="px-3 py-2 text-sm border rounded" style={{ borderColor: "var(--ph-border-strong)" }} value={profileEdit.timezone ?? ""} onChange={(e) => setProfileEdit({ ...profileEdit, timezone: e.target.value })} /></Field>
            <Field label="Website"><input data-testid="field-website" className="px-3 py-2 text-sm border rounded" style={{ borderColor: "var(--ph-border-strong)" }} value={profileEdit.website ?? ""} onChange={(e) => setProfileEdit({ ...profileEdit, website: e.target.value })} /></Field>
            <Field label="Mission" span={2}><textarea data-testid="field-mission" rows={2} className="px-3 py-2 text-sm border rounded" style={{ borderColor: "var(--ph-border-strong)" }} value={profileEdit.mission ?? ""} onChange={(e) => setProfileEdit({ ...profileEdit, mission: e.target.value })} /></Field>
            <Field label="Vision" span={2}><textarea data-testid="field-vision" rows={2} className="px-3 py-2 text-sm border rounded" style={{ borderColor: "var(--ph-border-strong)" }} value={profileEdit.vision ?? ""} onChange={(e) => setProfileEdit({ ...profileEdit, vision: e.target.value })} /></Field>
            <Field label="Values (comma-separated)" span={2}>
              <input
                data-testid="field-values"
                className="px-3 py-2 text-sm border rounded"
                style={{ borderColor: "var(--ph-border-strong)" }}
                value={Array.isArray(profileEdit.values) ? profileEdit.values.join(", ") : (profileEdit.values ?? "")}
                onChange={(e) => setProfileEdit({ ...profileEdit, values: e.target.value.split(",").map((s) => s.trim()).filter(Boolean) })}
              />
            </Field>
          </div>
        </Card>
      );
    }

    if (tab === "registration") {
      const rows = snapshot.registrations;
      return (
        <Card>
          <SectionHeader
            eyebrow="Commercial Registration"
            title="CR Ledger"
            action={
              <div className="flex gap-2">
                <AIExtractButton
                  ventureId={ventureId}
                  kind="cr"
                  onConfirm={async (extracted) => {
                    // Map extracted → registration schema
                    const payload: Record<string, unknown> = {
                      crNumber: extracted.crNumber,
                      issueDate: extracted.issueDate,
                      expiryDate: extracted.expiryDate,
                      issuingAuthority: extracted.issuingAuthority,
                      unifiedNumber: extracted.unifiedNumber,
                      commercialActivities: extracted.commercialActivities || [],
                      aiExtracted: true,
                    };
                    await handleCreate("registration", payload);
                  }}
                />
                <Button variant="primary" onClick={() => openCreate("registration")} testId="add-registration"><Plus size={14} /> Add CR</Button>
              </div>
            }
          />
          <DataTable
            testIdPrefix="registration"
            columns={[
              { key: "crNumber", label: "CR Number" },
              { key: "issuingAuthority", label: "Authority" },
              { key: "issueDate", label: "Issued", render: (r) => fmtDate(r.issueDate) },
              { key: "expiryDate", label: "Expires", render: (r) => {
                const d = daysUntil(r.expiryDate);
                if (d === null) return "—";
                if (d < 0) return <span>{fmtDate(r.expiryDate)} <Pill tone="danger">Expired {-d}d ago</Pill></span>;
                if (d < 30) return <span>{fmtDate(r.expiryDate)} <Pill tone="warning">{d}d left</Pill></span>;
                return <span>{fmtDate(r.expiryDate)} <Pill tone="success">OK</Pill></span>;
              } },
              { key: "aiExtracted", label: "Source", render: (r) => r.aiExtracted ? <Pill tone="info">AI</Pill> : <Pill>Manual</Pill> },
            ]}
            rows={rows}
            onRowClick={(r) => openEdit("registration", r)}
            onDelete={(r) => handleDelete("registration", r.id)}
          />
        </Card>
      );
    }

    if (tab === "tax") {
      return (
        <Card>
          <SectionHeader
            eyebrow="Tax Profile"
            title="Tax & VAT Records"
            action={
              <div className="flex gap-2">
                <AIExtractButton
                  ventureId={ventureId} kind="tax"
                  onConfirm={async (extracted) => { await handleCreate("tax", { ...extracted, aiExtracted: true }); }}
                />
                <Button variant="primary" onClick={() => openCreate("tax")} testId="add-tax"><Plus size={14} /> Add Tax Record</Button>
              </div>
            }
          />
          <DataTable
            testIdPrefix="tax"
            columns={[
              { key: "taxNumber", label: "Tax Number" },
              { key: "vatNumber", label: "VAT Number" },
              { key: "taxOffice", label: "Office" },
              { key: "vatRate", label: "VAT %", render: (r) => `${r.vatRate ?? "—"}%` },
              { key: "taxStatus", label: "Status", render: (r) => <Pill tone={r.taxStatus === "active" ? "success" : "warning"}>{r.taxStatus || "—"}</Pill> },
            ]}
            rows={snapshot.taxes}
            onRowClick={(r) => openEdit("tax", r)}
            onDelete={(r) => handleDelete("tax", r.id)}
          />
        </Card>
      );
    }

    if (tab === "banking") {
      return (
        <Card>
          <SectionHeader
            eyebrow="Banking"
            title="Corporate Bank Accounts"
            action={<Button variant="primary" onClick={() => openCreate("banking")} testId="add-banking"><Plus size={14} /> Add Account</Button>}
          />
          <DataTable
            testIdPrefix="banking"
            columns={[
              { key: "bank", label: "Bank" },
              { key: "beneficiary", label: "Beneficiary" },
              { key: "iban", label: "IBAN" },
              { key: "currency", label: "Currency" },
              { key: "purpose", label: "Purpose" },
              { key: "isPrimary", label: "Primary", render: (r) => r.isPrimary ? <Pill tone="success">Primary</Pill> : "—" },
            ]}
            rows={snapshot.banking}
            onRowClick={(r) => openEdit("banking", r)}
            onDelete={(r) => handleDelete("banking", r.id)}
          />
        </Card>
      );
    }

    if (tab === "addresses") {
      return (
        <Card>
          <SectionHeader
            eyebrow="Addresses"
            title="HQ / Warehouses / Factories / Branches"
            action={<Button variant="primary" onClick={() => openCreate("addresses")} testId="add-address"><Plus size={14} /> Add Address</Button>}
          />
          <DataTable
            testIdPrefix="addresses"
            columns={[
              { key: "kind", label: "Kind", render: (r) => <Pill tone="info">{r.kind}</Pill> },
              { key: "label", label: "Label" },
              { key: "city", label: "City" },
              { key: "district", label: "District" },
              { key: "street", label: "Street" },
              { key: "isPrimary", label: "Primary", render: (r) => r.isPrimary ? <Pill tone="success">Primary</Pill> : "—" },
            ]}
            rows={snapshot.addresses}
            onRowClick={(r) => openEdit("addresses", r)}
            onDelete={(r) => handleDelete("addresses", r.id)}
          />
        </Card>
      );
    }

    if (tab === "contacts") {
      return (
        <Card>
          <SectionHeader
            eyebrow="Official Contacts"
            title="Key People"
            action={<Button variant="primary" onClick={() => openCreate("contacts")} testId="add-contact"><Plus size={14} /> Add Contact</Button>}
          />
          <DataTable
            testIdPrefix="contacts"
            columns={[
              { key: "role", label: "Role", render: (r) => <Pill tone="info">{r.role}</Pill> },
              { key: "name", label: "Name" },
              { key: "email", label: "Email" },
              { key: "phone", label: "Phone" },
            ]}
            rows={snapshot.contacts}
            onRowClick={(r) => openEdit("contacts", r)}
            onDelete={(r) => handleDelete("contacts", r.id)}
          />
        </Card>
      );
    }

    if (tab === "licenses") {
      return (
        <Card>
          <SectionHeader
            eyebrow="Regulatory Licenses"
            title="Licenses & Certifications"
            action={
              <div className="flex gap-2">
                <AIExtractButton
                  ventureId={ventureId} kind="license"
                  onConfirm={async (extracted) => { await handleCreate("licenses", { ...extracted, status: "active", aiExtracted: true }); }}
                />
                <Button variant="primary" onClick={() => openCreate("licenses")} testId="add-license"><Plus size={14} /> Add License</Button>
              </div>
            }
          />
          <DataTable
            testIdPrefix="licenses"
            columns={[
              { key: "kind", label: "Kind", render: (r) => <Pill tone="info">{r.kind}</Pill> },
              { key: "licenseNumber", label: "License #" },
              { key: "issuer", label: "Issuer" },
              { key: "expiryDate", label: "Expiry", render: (r) => {
                const d = daysUntil(r.expiryDate);
                if (d === null) return "—";
                if (d < 0) return <span>{fmtDate(r.expiryDate)} <Pill tone="danger">Expired</Pill></span>;
                if (d < 30) return <span>{fmtDate(r.expiryDate)} <Pill tone="warning">{d}d</Pill></span>;
                return <span>{fmtDate(r.expiryDate)} <Pill tone="success">OK</Pill></span>;
              } },
              { key: "status", label: "Status" },
            ]}
            rows={snapshot.licenses}
            onRowClick={(r) => openEdit("licenses", r)}
            onDelete={(r) => handleDelete("licenses", r.id)}
          />
        </Card>
      );
    }

    if (tab === "government") {
      return (
        <Card>
          <SectionHeader
            eyebrow="Government Portals"
            title="Portal Accounts"
            action={<Button variant="primary" onClick={() => openCreate("government")} testId="add-gov"><Plus size={14} /> Add Portal</Button>}
          />
          <DataTable
            testIdPrefix="government"
            columns={[
              { key: "portal", label: "Portal", render: (r) => <Pill tone="info">{r.portal}</Pill> },
              { key: "displayName", label: "Name" },
              { key: "accountRef", label: "Account Ref" },
              { key: "status", label: "Status" },
            ]}
            rows={snapshot.government}
            onRowClick={(r) => openEdit("government", r)}
            onDelete={(r) => handleDelete("government", r.id)}
          />
        </Card>
      );
    }

    if (tab === "digital") {
      return (
        <Card>
          <SectionHeader
            eyebrow="Digital Identity"
            title="Domains · Websites · Social"
            action={<Button variant="primary" onClick={() => openCreate("digital")} testId="add-digital"><Plus size={14} /> Add Identity</Button>}
          />
          <DataTable
            testIdPrefix="digital"
            columns={[
              { key: "kind", label: "Kind", render: (r) => <Pill tone="info">{r.kind}</Pill> },
              { key: "handle", label: "Handle" },
              { key: "url", label: "URL", render: (r) => r.url ? <a href={r.url} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()} className="underline text-slate-700">{r.url}</a> : "—" },
              { key: "status", label: "Status" },
            ]}
            rows={snapshot.digital}
            onRowClick={(r) => openEdit("digital", r)}
            onDelete={(r) => handleDelete("digital", r.id)}
          />
        </Card>
      );
    }

    if (tab === "timeline") {
      return (
        <Card>
          <SectionHeader
            eyebrow="Company Timeline"
            title="History & Milestones"
            action={<Button variant="primary" onClick={() => setShowTimelineForm(true)} testId="add-timeline"><Plus size={14} /> Add Event</Button>}
          />
          {showTimelineForm && (
            <TimelineForm
              ventureId={ventureId}
              onClose={() => setShowTimelineForm(false)}
              onSaved={async () => { setShowTimelineForm(false); await load(); showFlash("Timeline event added."); }}
            />
          )}
          {snapshot.timeline.length === 0 ? (
            <EmptyState title="No events yet" hint="Add the first company milestone." />
          ) : (
            <ol className="relative pl-6 space-y-4 mt-4">
              {snapshot.timeline.map((ev: any) => (
                <li key={ev.id} className="relative" data-testid={`timeline-event-${ev.id}`}>
                  <span className="absolute -left-6 top-1.5 w-3 h-3 rounded-full" style={{ background: "var(--ph-brand-primary)" }} />
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <div className="text-[11px] uppercase tracking-[0.16em] text-slate-500 font-semibold flex items-center gap-2">
                        {fmtDate(ev.at)}
                        <Pill tone="info">{ev.category}</Pill>
                        {ev.pinned && <Pin size={11} className="text-amber-500" />}
                      </div>
                      <div className="text-base font-semibold text-slate-900 mt-0.5" style={{ fontFamily: "var(--ph-display-font)" }}>{ev.title}</div>
                      {ev.body && <div className="text-sm text-slate-700 mt-1">{ev.body}</div>}
                    </div>
                  </div>
                </li>
              ))}
            </ol>
          )}
        </Card>
      );
    }

    if (tab === "health") {
      return (
        <div className="grid md:grid-cols-2 gap-4">
          <Card>
            <SectionHeader
              eyebrow="Corporate Identity Health"
              title="Overall Score"
              action={<Button variant="secondary" onClick={handleRecomputeHealth} testId="recompute-health"><RefreshCcw size={14} /> Recompute</Button>}
            />
            {!health ? <EmptyState title="No score" hint="Click Recompute to calculate." /> : (
              <div className="space-y-3">
                <div className="flex items-center gap-4">
                  <ScoreDial score={health.healthScore} />
                  <div>
                    <div className="text-3xl font-bold text-slate-900" data-testid="health-score">{health.healthScore}<span className="text-lg text-slate-500">/100</span></div>
                    <div className="text-[11px] uppercase tracking-[0.16em] text-slate-500">Last computed · {fmtDate(health.computedAt)}</div>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-2 pt-3 border-t" style={{ borderColor: "var(--ph-border-subtle)" }}>
                  {Object.entries(health.breakdown).map(([k, v]) => (
                    <div key={k}>
                      <div className="text-[10px] uppercase tracking-[0.16em] text-slate-500 font-semibold">{k.replace(/([A-Z])/g, " $1").trim()}</div>
                      <div className="text-lg font-semibold text-slate-900">{v}%</div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </Card>
          <Card>
            <SectionHeader eyebrow="AI Recommendations" title="Next Best Actions" />
            {!health ? <EmptyState title="Nothing to show" hint="Recompute the health score first." /> : (
              <ul className="space-y-2">
                {health.recommendedActions.map((r, i) => (
                  <li key={i} className="text-sm text-slate-700 flex items-start gap-2" data-testid={`rec-action-${i}`}>
                    <Sparkles size={14} className="text-amber-500 mt-0.5 shrink-0" />
                    <span>{r}</span>
                  </li>
                ))}
              </ul>
            )}
            {health && (health.expired.length > 0 || health.upcoming30d.length > 0) && (
              <div className="mt-4 pt-4 border-t" style={{ borderColor: "var(--ph-border-subtle)" }}>
                {health.expired.length > 0 && (
                  <div className="mb-3">
                    <div className="text-[10px] uppercase tracking-[0.16em] text-red-600 font-semibold mb-1">Expired ({health.expired.length})</div>
                    <ul className="space-y-1 text-sm">
                      {health.expired.map((x, i) => (
                        <li key={i} className="flex items-center gap-2" data-testid={`expired-${i}`}>
                          <AlertTriangle size={12} className="text-red-500" />
                          <span className="text-slate-700">{x.kind} · {fmtDate(x.expiryDate)} · {x.daysAgo}d ago</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                {health.upcoming30d.length > 0 && (
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.16em] text-amber-600 font-semibold mb-1">Expiring in 30 days ({health.upcoming30d.length})</div>
                    <ul className="space-y-1 text-sm">
                      {health.upcoming30d.map((x, i) => (
                        <li key={i} className="flex items-center gap-2" data-testid={`upcoming-${i}`}>
                          <Clock size={12} className="text-amber-500" />
                          <span className="text-slate-700">{x.kind} · {fmtDate(x.expiryDate)} · in {x.daysAway}d</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}
          </Card>
        </div>
      );
    }

    return null;
  };

  const showExportMenu = useShowExport();

  return (
    <div className="pureh-company-setup" data-testid="company-setup-root">
      {/* Header strip */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
        <div>
          <div className="text-[10px] uppercase tracking-[0.16em] text-slate-500 font-semibold">Workstream 01</div>
          <h1 className="text-3xl font-semibold text-slate-900" style={{ fontFamily: "var(--ph-display-font)", letterSpacing: "0.01em" }} data-testid="page-title">
            Company Setup
          </h1>
          <div className="text-sm text-slate-500 mt-1">Onboard every legal, financial and regulatory detail of the venture.</div>
        </div>
        <div className="flex items-center gap-3">
          <label className="text-[10px] uppercase tracking-[0.16em] text-slate-500 font-semibold">Venture</label>
          <select
            value={ventureId}
            onChange={(e) => setVentureId(e.target.value)}
            className="px-3 py-2 text-sm border rounded"
            style={{ borderColor: "var(--ph-border-strong)" }}
            data-testid="venture-select"
          >
            {ventures.map((v) => <option key={v.id} value={v.id}>{v.name}</option>)}
          </select>
          {health && <ScoreDial score={health.healthScore} />}
          <div className="relative">
            <Button variant="secondary" onClick={showExportMenu.toggle} testId="export-dropdown-btn">
              <Download size={14} /> Export
            </Button>
            {showExportMenu.open && (
              <div className="absolute right-0 mt-2 w-56 bg-white border rounded shadow-lg z-30" style={{ borderColor: "var(--ph-border-strong)" }}>
                <button onClick={() => { showExportMenu.close(); handleDownload("company-profile"); }} className="w-full text-left px-3 py-2 text-sm hover:bg-slate-50" data-testid="export-profile">Company Profile PDF</button>
                <button onClick={() => { showExportMenu.close(); handleDownload("dd-package"); }} className="w-full text-left px-3 py-2 text-sm hover:bg-slate-50" data-testid="export-dd">DD Package PDF</button>
                <button onClick={() => { showExportMenu.close(); handleDownload("corporate-info"); }} className="w-full text-left px-3 py-2 text-sm hover:bg-slate-50" data-testid="export-info">Corporate Info PDF</button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Missing / expired alert strip */}
      {health && (health.missing.length > 0 || health.expired.length > 0) && (
        <Card className="mb-6" testId="health-alert">
          <div className="flex items-start gap-3">
            <AlertTriangle size={20} className="text-amber-500 shrink-0 mt-0.5" />
            <div className="flex-1">
              <div className="text-sm font-semibold text-slate-900">Attention required</div>
              <div className="text-xs text-slate-600 mt-0.5">
                {health.expired.length > 0 && <span className="text-red-600 mr-3">⚠️ {health.expired.length} expired document(s)</span>}
                {health.upcoming30d.length > 0 && <span className="text-amber-600 mr-3">🕒 {health.upcoming30d.length} expiring in 30d</span>}
                {health.missing.length > 0 && <span className="text-slate-600">➕ {health.missing.length} missing record(s)</span>}
              </div>
            </div>
            <Button variant="secondary" onClick={() => setTab("health")} testId="jump-health">Review</Button>
          </div>
        </Card>
      )}

      {/* Tabs */}
      <div className="flex gap-1 border-b overflow-x-auto mb-5" style={{ borderColor: "var(--ph-border-subtle)" }}>
        {TABS.map((t) => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            className="flex items-center gap-2 px-4 py-2 text-sm font-medium whitespace-nowrap"
            style={{
              color: tab === t.key ? "var(--ph-ink-strong)" : "var(--ph-ink-muted)",
              borderBottom: tab === t.key ? "2px solid var(--ph-brand-primary)" : "2px solid transparent",
            }}
            data-testid={`tab-${t.key}`}
          >
            <t.Icon size={14} /> {t.label}
          </button>
        ))}
      </div>

      {/* Tab content */}
      <div data-testid={`tab-content-${tab}`}>
        {loading ? <EmptyState title="Loading…" /> : renderTab()}
      </div>

      {/* Section drawer */}
      {drawer && (
        <SectionDrawer
          open={!!drawer}
          onClose={() => setDrawer(null)}
          title={drawer.initial.id ? `Edit ${drawer.section}` : `Add ${drawer.section}`}
          fields={SECTION_FIELDS[drawer.section]}
          initial={drawer.initial}
          ventureId={ventureId}
          section={drawer.section}
          onSave={async (row) => {
            const clean: Record<string, unknown> = {};
            for (const f of SECTION_FIELDS[drawer.section]) {
              let v = row[f.key];
              if (f.key === "commercialActivities" && typeof v === "string") {
                v = (v as string).split(",").map((s) => s.trim()).filter(Boolean);
              }
              if (v !== undefined) clean[f.key] = v;
            }
            if (drawer.initial.id) {
              await handlePatch(drawer.section, drawer.initial.id as string, clean);
            } else {
              await handleCreate(drawer.section, clean);
            }
          }}
        />
      )}

      {/* Flash toast */}
      {flash && (
        <div className="fixed bottom-6 right-6 px-4 py-2 bg-slate-900 text-white text-sm rounded shadow-lg z-50" data-testid="flash-toast">
          {flash}
        </div>
      )}
    </div>
  );
}

// ── Small hook for the export dropdown ───────────────────────────
function useShowExport() {
  const [open, setOpen] = useState(false);
  useEffect(() => {
    if (!open) return;
    const handler = () => setOpen(false);
    // Close on next tick to avoid immediate close on the trigger click
    const t = setTimeout(() => window.addEventListener("click", handler, { once: true }), 0);
    return () => { clearTimeout(t); window.removeEventListener("click", handler); };
  }, [open]);
  return { open, toggle: () => setOpen((o) => !o), close: () => setOpen(false) };
}

// ── Timeline event form ──────────────────────────────────────────
function TimelineForm({ ventureId, onClose, onSaved }: { ventureId: string; onClose: () => void; onSaved: () => void | Promise<void>; }) {
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [category, setCategory] = useState("milestone");
  const [at, setAt] = useState(new Date().toISOString().slice(0, 10));
  const [pinned, setPinned] = useState(false);
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!title.trim()) return;
    setSaving(true);
    try {
      await corporateIdentity.addTimelineEvent(ventureId, { title, body, category, at, pinned });
      await onSaved();
    } finally { setSaving(false); }
  };

  return (
    <div className="p-4 rounded mb-4" style={{ background: "var(--ph-surface-alt)", border: "1px dashed var(--ph-border-strong)" }} data-testid="timeline-form">
      <div className="grid grid-cols-2 gap-3">
        <Field label="Title" span={2}><input data-testid="tl-title" className="px-3 py-2 text-sm border rounded" style={{ borderColor: "var(--ph-border-strong)" }} value={title} onChange={(e) => setTitle(e.target.value)} /></Field>
        <Field label="Category">
          <select data-testid="tl-category" className="px-3 py-2 text-sm border rounded" style={{ borderColor: "var(--ph-border-strong)" }} value={category} onChange={(e) => setCategory(e.target.value)}>
            <option>founding</option><option>legal</option><option>investment</option><option>operations</option><option>milestone</option><option>exit</option><option>people</option><option>regulatory</option>
          </select>
        </Field>
        <Field label="Date"><input type="date" data-testid="tl-date" className="px-3 py-2 text-sm border rounded" style={{ borderColor: "var(--ph-border-strong)" }} value={at} onChange={(e) => setAt(e.target.value)} /></Field>
        <Field label="Body" span={2}><textarea data-testid="tl-body" rows={2} className="px-3 py-2 text-sm border rounded" style={{ borderColor: "var(--ph-border-strong)" }} value={body} onChange={(e) => setBody(e.target.value)} /></Field>
        <label className="col-span-2 flex items-center gap-2 text-sm text-slate-700">
          <input type="checkbox" checked={pinned} onChange={(e) => setPinned(e.target.checked)} data-testid="tl-pinned" /> Pin this event
        </label>
      </div>
      <div className="mt-3 flex justify-end gap-2">
        <Button variant="secondary" onClick={onClose} testId="tl-cancel">Cancel</Button>
        <Button variant="primary" onClick={save} testId="tl-save" disabled={saving || !title.trim()}>
          <Save size={14} /> {saving ? "Saving…" : "Add Event"}
        </Button>
      </div>
    </div>
  );
}
